import {
    styled,
    Box,
    Typography,
    Alert,
    Divider,
    Skeleton,
    Chip,
    Select,
    MenuItem,
    ListSubheader,
    Tooltip,
    TextField,
} from '@mui/material';
import {
    useEffect, useState, useRef,
} from 'react';
import { TitledAppBar } from 'src/components/common/TitledAppBar';
import { DocumentSourceInputs } from 'src/components/MatchingVisualizer/DocumentSourceInput';
import { LoadResetButtons } from 'src/components/MatchingVisualizer/LoadResetButtons';
import { useMatchingVisualizerLoad } from 'src/hooks/useMatchingVisualizerLoad';
import { useCompositeMatching } from 'src/hooks/useCompositeMatching';
import { useDocumentSource } from 'src/hooks/useDocumentSource';
import { TargetProductOptions } from 'src/components/MatchingVisualizer/TargetProductOptions';
import { JsonViewer } from 'src/components/MatchingVisualizer/JsonViewer';
import { DEFAULT_CULTURE } from 'src/constants';
import { AssetsImages } from 'src/components/MatchingVisualizer/AssetsImages';
import { LoadingStateImage } from 'src/components/MatchingVisualizer/LoadingStateImage';
import { DestinationProductOptions } from 'src/components/MatchingVisualizer/DestinationProductOptions';
import { PreviewUrlBuilder } from 'src/utils/PreviewUrlBuilder';
import { CompositeMatchingType } from 'src/types/compositeMatching';
import { MATCHING_PRESETS, MatchingPreset } from 'src/data/matchingPresets';

const StyledContainer = styled(Box)(({ theme }) => ({
    padding: `0 ${theme.spacing(8)}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    height: `calc(100vh - ${theme.spacing(20)})`,
}));

const ContentWrapper = styled(Box)(() => ({
    width: '100%',
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
}));

const TwoColumnLayout = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
    },
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
}));

const LeftColumn = styled(Box)(({ theme }) => ({
    flex: `0 0 calc(40% - ${theme.spacing(2)})`,
    minHeight: 0,
    display: 'flex',
    [theme.breakpoints.down('md')]: {
        flex: '1 1 auto',
    },
}));

const RightColumn = styled(Box)(({ theme }) => ({
    flex: `0 0 calc(60% - ${theme.spacing(2)})`,
    minWidth: 0,
    overflow: 'auto',
    [theme.breakpoints.down('md')]: {
        flex: '1 1 auto',
    },
}));

const PlaceholderCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const MATCHER_LABELS: Record<CompositeMatchingType, string> = {
    Template: 'Source Matching',
    Resizer: 'Source Matching (Resized)',
    Logo: 'Logo Matching',
    Photo: 'Photo Matching',
    AGGenerate: 'Composition Matching',
};

const getMatcherLabel = (matchingType: CompositeMatchingType): string => MATCHER_LABELS[matchingType] ?? matchingType;

export const MatchingVisualizerPage = (): JSX.Element => {
    const [productKey, setProductKey] = useState<string>('');
    const [version, setVersion] = useState<number | undefined>(undefined);
    const leftPanelRef = useRef<HTMLDivElement>(null);
    const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
    const [culture, setCulture] = useState<string>(DEFAULT_CULTURE);
    const [uri, setUri] = useState('');
    const [pdsResult, setPdsResult] = useState('');
    const [showPdsResult, setShowPdsResult] = useState(false);
    const [dpcId, setDpcId] = useState('');
    const [showDpcId, setShowDpcId] = useState(false);
    const [designConceptIdInput, setDesignConceptIdInput] = useState('');
    const [showDesignConceptId, setShowDesignConceptId] = useState(false);
    const [brandId, setBrandId] = useState('');
    const [showBrandId, setShowBrandId] = useState(false);
    const [sourceProductKey, setSourceProductKey] = useState('');
    const [sourceProductVersion, setSourceProductVersion] = useState<number | undefined>(undefined);
    const [sourceProductOptions, setSourceProductOptions] = useState<Record<string, string>>({});
    const [showSourceProduct, setShowSourceProduct] = useState(false);
    const [selectedMatcherType, setSelectedMatcherType] = useState<CompositeMatchingType | 'siteResult' | null>(null);
    const [selectedPresetIndex, setSelectedPresetIndex] = useState<number | ''>('');
    const [loadParams, setLoadParams] = useState<{
        documentSourceUri: string;
        productKey: string;
        productVersion: number;
        culture: string;
        fullProductSpecification: Record<string, string>;
        pdsResultUri?: string;
        dpcId?: string;
        designConceptId?: string;
        brandId?: string;
        sourceProductKey?: string;
        sourceProductVersion?: number;
        sourceProductOptions?: Record<string, string>;
    } | null>(null);

    // Shared document source query
    const documentSourceQuery = useDocumentSource(uri);

    // Only call matching hook if document source is successful
    const matchingResults = useMatchingVisualizerLoad(
        loadParams && !documentSourceQuery.error ? loadParams : null,
        documentSourceQuery,
    );

    const compositeDpcId = matchingResults.dpcmResult.data?.designPersonalizationContextId ?? loadParams?.dpcId;
    const compositeDesignConceptId = loadParams?.designConceptId || matchingResults.templateDiscoveryResult.designConceptId || undefined;
    const compositeBrandId = loadParams?.brandId || undefined;
    const hasCompositeSource = !!compositeDpcId || !!compositeDesignConceptId || !!compositeBrandId;
    const compositeMatchingQuery = useCompositeMatching(
        loadParams && !documentSourceQuery.error && hasCompositeSource
            ? {
                productKey: loadParams.productKey,
                productVersion: loadParams.productVersion,
                culture: loadParams.culture,
                productOptions: loadParams.fullProductSpecification,
                designPersonalizationContextId: compositeDpcId,
                designConceptId: compositeDesignConceptId,
                brandId: compositeBrandId,
                sourceProductKey: loadParams.sourceProductKey || undefined,
                sourceProductVersion: loadParams.sourceProductVersion,
                sourceProductOptions: loadParams.sourceProductOptions,
            }
            : null,
    );

    const compositeMatches = compositeMatchingQuery.data?.matches ?? [];
    const hasSiteResultPreview = !!matchingResults.pdsResultPreview.previewUrl;
    const showSiteResult = selectedMatcherType === 'siteResult';
    const selectedMatch = showSiteResult ? null : compositeMatches.find((m) => m.matchingType === selectedMatcherType);
    const winnerType = compositeMatches.find((m) => m.match !== null)?.matchingType ?? null;

    useEffect(() => {
        if (compositeMatchingQuery.data && !selectedMatcherType) {
            const { matches } = compositeMatchingQuery.data;
            const winner = matches.find((m) => m.match !== null);

            setSelectedMatcherType(winner?.matchingType ?? matches[0]?.matchingType ?? null);
        }
    }, [compositeMatchingQuery.data, selectedMatcherType]);

    useEffect(() => {
        if (showSiteResult && !hasSiteResultPreview) {
            setSelectedMatcherType(winnerType);
        }
    }, [showSiteResult, hasSiteResultPreview, winnerType]);

    const assetsInfo = matchingResults.dpcmResult.data?.designPersonalizationData?.images[0]?.image;
    const assetsPreviewUrl = assetsInfo?.previewUrl || '';
    const assetsPrintUrl = assetsInfo?.printUrl || '';
    const assetsOriginalUrl = assetsInfo?.originalSourceUrl || '';

    const isStaticDesign = documentSourceQuery.data?.templateIdentifier?.type === 'TEMPLATE_TOKEN';
    const templateToken = isStaticDesign
        ? (documentSourceQuery.data?.templateIdentifier?.value ?? undefined)
        : undefined;
    const originalSourceTemplateUrl = templateToken
        ? PreviewUrlBuilder.build(templateToken, '400')
        : undefined;

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        const documentUri = params.get('documentUri');
        const productKeyParam = params.get('productKey');
        const versionParam = params.get('productVersion');
        const cultureParam = params.get('culture');
        const optionsParam = params.get('options');
        const pdsResultParam = params.get('siteResult');
        const dpcIdParam = params.get('dpcId');
        const designConceptIdParam = params.get('designConceptId');
        const brandIdParam = params.get('brandId');
        const sourceProductKeyParam = params.get('sourceProductKey');
        const sourceProductVersionParam = params.get('sourceProductVersion');
        const sourceProductOptionsParam = params.get('sourceProductOptions');

        if (documentUri) {
            setUri(decodeURIComponent(documentUri));
        }
        if (productKeyParam) {
            setProductKey(decodeURIComponent(productKeyParam));
        }
        if (versionParam) {
            setVersion(Number(versionParam));
        }
        if (cultureParam) {
            setCulture(decodeURIComponent(cultureParam));
        }
        if (pdsResultParam) {
            setPdsResult(decodeURIComponent(pdsResultParam));
            setShowPdsResult(true);
        }
        if (dpcIdParam) {
            setDpcId(decodeURIComponent(dpcIdParam));
            setShowDpcId(true);
        }
        if (designConceptIdParam) {
            setDesignConceptIdInput(decodeURIComponent(designConceptIdParam));
            setShowDesignConceptId(true);
        }
        if (brandIdParam) {
            setBrandId(decodeURIComponent(brandIdParam));
            setShowBrandId(true);
        }
        if (sourceProductKeyParam) {
            setSourceProductKey(decodeURIComponent(sourceProductKeyParam));
            setShowSourceProduct(true);
        }
        if (sourceProductVersionParam) {
            setSourceProductVersion(Number(sourceProductVersionParam));
            setShowSourceProduct(true);
        }
        if (sourceProductOptionsParam) {
            try {
                const parsed = JSON.parse(decodeURIComponent(sourceProductOptionsParam));

                if (parsed && typeof parsed === 'object') {
                    setSourceProductOptions(parsed);
                    setShowSourceProduct(true);
                }
            } catch {
                setSourceProductOptions({});
            }
        }

        if (optionsParam) {
            try {
                const decoded = decodeURIComponent(optionsParam);
                const parsed = JSON.parse(decoded);

                if (parsed && typeof parsed === 'object') {
                    setSelectedOptions(parsed);
                }
            } catch {
                setSelectedOptions({});
            }
        }

        const hasSourceParam = !!(documentUri || dpcIdParam || designConceptIdParam || brandIdParam);

        if (hasSourceParam && productKeyParam && versionParam && cultureParam && optionsParam) {
            const parsedOptions = JSON.parse(decodeURIComponent(optionsParam)) as Record<string, string>;

            setLoadParams({
                documentSourceUri: documentUri ? decodeURIComponent(documentUri) : '',
                productKey: decodeURIComponent(productKeyParam),
                productVersion: Number(versionParam),
                culture: decodeURIComponent(cultureParam),
                fullProductSpecification: parsedOptions,
                pdsResultUri: pdsResultParam ? decodeURIComponent(pdsResultParam) : undefined,
                dpcId: dpcIdParam ? decodeURIComponent(dpcIdParam) : undefined,
                designConceptId: designConceptIdParam ? decodeURIComponent(designConceptIdParam) : undefined,
                brandId: brandIdParam ? decodeURIComponent(brandIdParam) : undefined,
                sourceProductKey: sourceProductKeyParam ? decodeURIComponent(sourceProductKeyParam) : undefined,
                sourceProductVersion: sourceProductVersionParam ? Number(sourceProductVersionParam) : undefined,
                sourceProductOptions: sourceProductOptionsParam
                    ? ((): Record<string, string> | undefined => {
                        try { return JSON.parse(decodeURIComponent(sourceProductOptionsParam)); } catch { return undefined; }
                    })()
                    : undefined,
            });
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams();

        if (uri) {
            params.set('documentUri', encodeURIComponent(uri));
        }
        if (productKey) {
            params.set('productKey', encodeURIComponent(productKey));
        }
        if (version !== undefined) {
            params.set('productVersion', String(version));
        }
        if (culture) {
            params.set('culture', encodeURIComponent(culture));
        }
        if (Object.keys(selectedOptions).length > 0) {
            params.set('options', encodeURIComponent(JSON.stringify(selectedOptions)));
        }
        if (pdsResult) {
            params.set('siteResult', encodeURIComponent(pdsResult));
        }
        if (dpcId) {
            params.set('dpcId', encodeURIComponent(dpcId));
        }
        if (designConceptIdInput) {
            params.set('designConceptId', encodeURIComponent(designConceptIdInput));
        }
        if (brandId) {
            params.set('brandId', encodeURIComponent(brandId));
        }
        if (sourceProductKey) {
            params.set('sourceProductKey', encodeURIComponent(sourceProductKey));
        }
        if (sourceProductVersion !== undefined) {
            params.set('sourceProductVersion', String(sourceProductVersion));
        }
        if (Object.keys(sourceProductOptions).length > 0) {
            params.set('sourceProductOptions', encodeURIComponent(JSON.stringify(sourceProductOptions)));
        }

        const newUrl = `${window.location.pathname}?${params.toString()}`;

        window.history.replaceState({}, '', newUrl);
    }, [uri, productKey, version, culture, selectedOptions, pdsResult, dpcId, designConceptIdInput, brandId, sourceProductKey, sourceProductVersion, sourceProductOptions]);

    const handleLoad = (documentUri: string): void => {
        if (!productKey || !version) {
            return;
        }

        const nextParams = {
            documentSourceUri: documentUri,
            productKey,
            productVersion: version,
            culture,
            fullProductSpecification: selectedOptions,
            pdsResultUri: pdsResult || undefined,
            dpcId: dpcId || undefined,
            designConceptId: designConceptIdInput || undefined,
            brandId: brandId || undefined,
            sourceProductKey: sourceProductKey || undefined,
            sourceProductVersion,
            sourceProductOptions: Object.keys(sourceProductOptions).length > 0 ? sourceProductOptions : undefined,
        };

        setSelectedMatcherType(null);
        setLoadParams(null);
        queueMicrotask(() => setLoadParams(nextParams));
    };

    const handleReset = (): void => {
        setUri('');
        setProductKey('');
        setVersion(undefined);
        setSelectedOptions({});
        setCulture(DEFAULT_CULTURE);
        setPdsResult('');
        setShowPdsResult(false);
        setDpcId('');
        setShowDpcId(false);
        setDesignConceptIdInput('');
        setShowDesignConceptId(false);
        setBrandId('');
        setShowBrandId(false);
        setSourceProductKey('');
        setSourceProductVersion(undefined);
        setSourceProductOptions({});
        setShowSourceProduct(false);
        setSelectedMatcherType(null);
        setSelectedPresetIndex('');
        setLoadParams(null);
    };

    const handlePresetSelect = (index: number | ''): void => {
        if (index === '') return;

        const scrollTop = leftPanelRef.current?.scrollTop ?? 0;

        requestAnimationFrame(() => {
            if (leftPanelRef.current) {
                leftPanelRef.current.scrollTop = scrollTop;
            }
        });

        const preset: MatchingPreset = MATCHING_PRESETS[index];

        const presetProductOptions = preset.productOptions ?? {};

        setUri(preset.documentSourceUri);
        setProductKey(preset.productKey);
        setVersion(preset.productVersion);
        setCulture(preset.culture);
        setSelectedOptions(presetProductOptions);

        if (preset.siteResultUri) {
            setPdsResult(preset.siteResultUri);
            setShowPdsResult(true);
        } else {
            setPdsResult('');
            setShowPdsResult(false);
        }

        if (preset.dpcId) {
            setDpcId(preset.dpcId);
            setShowDpcId(true);
        } else {
            setDpcId('');
            setShowDpcId(false);
        }

        setDesignConceptIdInput('');
        setShowDesignConceptId(false);
        setBrandId('');
        setShowBrandId(false);

        const presetSourceProductKey = preset.sourceProductKey ?? '';
        const presetSourceProductVersion = preset.sourceProductVersion;
        const presetSourceProductOptions = preset.sourceProductOptions ?? {};
        const hasSourceProduct = !!preset.sourceProductKey
            || preset.sourceProductVersion !== undefined
            || Object.keys(presetSourceProductOptions).length > 0;

        setSourceProductKey(presetSourceProductKey);
        setSourceProductVersion(presetSourceProductVersion);
        setSourceProductOptions(presetSourceProductOptions);
        setShowSourceProduct(hasSourceProduct);

        setSelectedPresetIndex(index);
        setSelectedMatcherType(null);
        setLoadParams(null);
        queueMicrotask(() => setLoadParams({
            documentSourceUri: preset.documentSourceUri,
            productKey: preset.productKey,
            productVersion: preset.productVersion,
            culture: preset.culture,
            fullProductSpecification: presetProductOptions,
            pdsResultUri: preset.siteResultUri,
            dpcId: preset.dpcId,
            sourceProductKey: preset.sourceProductKey,
            sourceProductVersion: preset.sourceProductVersion,
            sourceProductOptions: Object.keys(presetSourceProductOptions).length > 0 ? presetSourceProductOptions : undefined,
        }));
    };

    const clearPreset = (): void => setSelectedPresetIndex('');

    return (
        <>
            <TitledAppBar title="Matching Visualizer" />
            <StyledContainer>
                <ContentWrapper>
                    <TwoColumnLayout>
                        <LeftColumn>
                            <PlaceholderCard sx={{
                                flexDirection: 'column', alignItems: 'stretch', height: '100%', overflow: 'hidden',
                            }}
                            >
                                <Box
                                    ref={leftPanelRef}
                                    sx={{
                                        flex: 1,
                                        overflow: 'auto',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        select
                                        label="Load Preset"
                                        size="small"
                                        sx={{ my: 2 }}
                                        value={selectedPresetIndex}
                                        onChange={(e): void => handlePresetSelect(e.target.value as unknown as number | '')}
                                    >
                                        {MATCHING_PRESETS.map((preset, index) => (
                                            <MenuItem key={preset.label} value={index}>
                                                {`${index + 1}. ${preset.label}`}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <DocumentSourceInputs
                                        brandId={brandId}
                                        designConceptId={designConceptIdInput}
                                        documentSourceQuery={documentSourceQuery}
                                        documentUri={uri}
                                        dpcId={dpcId}
                                        pdsResult={pdsResult}
                                        showBrandId={showBrandId}
                                        showDesignConceptId={showDesignConceptId}
                                        showDpcId={showDpcId}
                                        showPdsResult={showPdsResult}
                                        showSourceProduct={showSourceProduct}
                                        sourceProductKey={sourceProductKey}
                                        sourceProductOptions={sourceProductOptions}
                                        sourceProductVersion={sourceProductVersion}
                                        onBrandIdChange={(val: string): void => { setBrandId(val); clearPreset(); }}
                                        onDesignConceptIdChange={(val: string): void => { setDesignConceptIdInput(val); clearPreset(); }}
                                        onDocumentUriChange={(val: string): void => { setUri(val); clearPreset(); }}
                                        onDpcIdChange={(val: string): void => { setDpcId(val); clearPreset(); }}
                                        onPdsResultChange={(val: string): void => { setPdsResult(val); clearPreset(); }}
                                        onShowBrandIdChange={(show: boolean): void => setShowBrandId(show)}
                                        onShowDesignConceptIdChange={(show: boolean): void => setShowDesignConceptId(show)}
                                        onShowDpcIdChange={(show: boolean): void => setShowDpcId(show)}
                                        onShowPdsResultChange={(show: boolean): void => setShowPdsResult(show)}
                                        onShowSourceProductChange={(show: boolean): void => setShowSourceProduct(show)}
                                        onSourceProductKeyChange={(val: string): void => { setSourceProductKey(val); clearPreset(); }}
                                        onSourceProductOptionsChange={(opts: Record<string, string>): void => { setSourceProductOptions(opts); clearPreset(); }}
                                        onSourceProductVersionChange={(val: number | undefined): void => { setSourceProductVersion(val); clearPreset(); }}
                                    />
                                    <TargetProductOptions
                                        culture={culture}
                                        initialOptions={selectedOptions}
                                        key={`preset-${selectedPresetIndex}`}
                                        productKey={productKey}
                                        version={version}
                                        onChangeCulture={(c): void => setCulture(c)}
                                        onChangeOptions={(opts): void => setSelectedOptions(opts)}
                                        onChangeProductKey={(k): void => setProductKey(k)}
                                        onChangeVersion={(v): void => setVersion(v)}
                                    />
                                </Box>
                                <Box sx={{ flexShrink: 0, pt: 2 }}>
                                    <LoadResetButtons
                                        brandId={brandId}
                                        designConceptId={designConceptIdInput}
                                        dpcId={dpcId}
                                        hasAnyInput={
                                            !!uri
                                            || !!dpcId
                                            || !!designConceptIdInput
                                            || !!brandId
                                            || !!sourceProductKey
                                            || sourceProductVersion !== undefined
                                            || Object.keys(sourceProductOptions).length > 0
                                            || !!pdsResult
                                            || !!productKey
                                            || version !== undefined
                                            || culture !== DEFAULT_CULTURE
                                            || Object.keys(selectedOptions).length > 0
                                            || selectedPresetIndex !== ''
                                            || loadParams !== null
                                        }
                                        isLoading={matchingResults.isLoading}
                                        productKey={productKey}
                                        productVersion={version}
                                        uri={uri}
                                        onLoad={handleLoad}
                                        onReset={handleReset}
                                    />
                                </Box>
                            </PlaceholderCard>
                        </LeftColumn>
                        <RightColumn>
                            <PlaceholderCard sx={{
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                justifyContent: 'stretch',
                                gap: 2,
                                minHeight: '100%',
                            }}
                            >
                                {documentSourceQuery.error && (
                                    <Box sx={{ width: '100%' }}>
                                        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                                            {`Document Source Error: ${documentSourceQuery.error.message}`}
                                        </Alert>
                                    </Box>
                                )}
                                {matchingResults.isLoading && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Box>
                                            <Box sx={{
                                                display: 'flex', alignItems: 'center', gap: 2, mb: 1,
                                            }}
                                            >
                                                <Typography color="text.secondary" sx={{ fontWeight: 500 }} variant="body2">
                                                    Composite Matcher:
                                                </Typography>
                                                <Skeleton animation="wave" height={40} variant="rectangular" width={240} />
                                            </Box>
                                            <Divider sx={{ mt: 1, mb: 2 }} />
                                            <Box>
                                                <Box sx={{ mb: 2 }}>
                                                    <Skeleton
                                                        animation="wave"
                                                        height={36.5}
                                                        sx={{ borderRadius: 1 }}
                                                        variant="rectangular"
                                                        width="100%"
                                                    />
                                                </Box>
                                                <Typography gutterBottom variant="h6">
                                                    Destination Preview
                                                </Typography>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
                                                        <Skeleton
                                                            animation="wave"
                                                            height={400}
                                                            sx={{ borderRadius: 1 }}
                                                            variant="rectangular"
                                                            width={500}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Box sx={{ mt: 3 }}>
                                                <Typography gutterBottom variant="h6">
                                                    Destination Product Options
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {[1, 2, 3, 4].map((i) => (
                                                        <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                                                            <Skeleton animation="wave" height={40} width="40%" />
                                                            <Skeleton animation="wave" height={40} width="50%" />
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Divider sx={{ my: 2 }} />
                                        <Box>
                                            <Typography gutterBottom variant="h6">
                                                DocumentSource Preview
                                            </Typography>
                                            <Skeleton
                                                animation="wave"
                                                sx={{
                                                    width: '100%',
                                                    height: 300,
                                                    borderRadius: 1,
                                                }}
                                                variant="rectangular"
                                            />
                                        </Box>
                                        <Box>
                                            <Skeleton animation="wave" height={32} sx={{ mb: 1 }} width="60%" />
                                            <Skeleton
                                                animation="wave"
                                                sx={{
                                                    width: '100%',
                                                    height: 150,
                                                    borderRadius: 1,
                                                }}
                                                variant="rectangular"
                                            />
                                        </Box>
                                    </Box>
                                )}
                                {matchingResults.warnings.length > 0 && (
                                    <Box sx={{ width: '100%' }}>
                                        <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
                                            Warnings occurred during matching process:
                                            {matchingResults.warnings.map((warning, index) => (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <div key={`warning-${index}`}>
                                                    {`- ${warning}`}
                                                </div>
                                            ))}
                                        </Alert>
                                    </Box>
                                )}
                                {(matchingResults.hasError || compositeMatchingQuery.error) && (
                                    <Box sx={{ width: '100%' }}>
                                        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                                            Error occurred during matching process:
                                            {matchingResults.errors.map((error, index) => (
                                                error && (
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    <div key={`error-${index}`}>
                                                        {`- ${error.message}.`}
                                                    </div>
                                                )
                                            ))}
                                            {compositeMatchingQuery.error && (
                                                <div>
                                                    {`- Composite Matching: ${compositeMatchingQuery.error.message}.`}
                                                </div>
                                            )}
                                        </Alert>
                                    </Box>
                                )}
                                {!matchingResults.isLoading && !loadParams && !matchingResults.hasError && (
                                    <Typography color="text.secondary" textAlign="center" variant="body2">
                                        Click Load to see matching results
                                    </Typography>
                                )}
                                {loadParams && !documentSourceQuery.error && !matchingResults.isLoading
                                  && (
                                  <Box sx={{
                                      display: 'flex', flexDirection: 'column', gap: 2, width: '100%',
                                  }}
                                  >
                                      <Box>
                                          {!compositeMatchingQuery.error && (
                                          <Box sx={{
                                              display: 'flex', alignItems: 'center', gap: 2, mb: 1,
                                          }}
                                          >
                                              <Typography color="text.secondary" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }} variant="body2">
                                                  Composite Matcher:
                                              </Typography>
                                              {compositeMatchingQuery.isLoading && (
                                                  <Skeleton animation="wave" height={40} variant="rectangular" width={240} />
                                              )}
                                              {compositeMatchingQuery.data && selectedMatcherType && (
                                                  <Select
                                                      renderValue={(value): JSX.Element => {
                                                          if (value === 'siteResult') {
                                                              return <span>Site Result</span>;
                                                          }

                                                          const matcherValue = value as CompositeMatchingType;
                                                          const index = compositeMatches.findIndex((m) => m.matchingType === matcherValue);
                                                          const entry = compositeMatches[index];
                                                          const available = entry?.match !== null;

                                                          return (
                                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                  <Typography color="text.secondary" component="span" variant="caption">
                                                                      {`P${index + 1}`}
                                                                  </Typography>
                                                                  <span>{getMatcherLabel(matcherValue)}</span>
                                                                  {matcherValue === winnerType && (
                                                                  <Chip
                                                                      label="Selected"
                                                                      size="small"
                                                                      sx={{
                                                                          height: 20,
                                                                          fontSize: '0.7rem',
                                                                          backgroundColor: 'primary.main',
                                                                          color: 'white',
                                                                      }}
                                                                  />
                                                                  )}
                                                                  {available && matcherValue !== winnerType && (
                                                                  <Chip
                                                                      label="Available"
                                                                      size="small"
                                                                      sx={{
                                                                          height: 20,
                                                                          fontSize: '0.7rem',
                                                                          borderColor: 'primary.main',
                                                                          color: 'primary.main',
                                                                      }}
                                                                      variant="outlined"
                                                                  />
                                                                  )}
                                                                  {!available && (
                                                                  <Chip
                                                                      label="Unavailable"
                                                                      size="small"
                                                                      sx={{
                                                                          height: 20,
                                                                          fontSize: '0.7rem',
                                                                          borderColor: 'error.main',
                                                                          color: 'error.main',
                                                                      }}
                                                                      variant="outlined"
                                                                  />
                                                                  )}
                                                              </Box>
                                                          );
                                                      }}
                                                      size="small"
                                                      sx={{ width: 320 }}
                                                      value={selectedMatcherType}
                                                      onChange={(e): void => setSelectedMatcherType(e.target.value as CompositeMatchingType | 'siteResult')}
                                                  >
                                                      {compositeMatches.map((entry, index) => {
                                                          const available = entry.match !== null;
                                                          const isWinner = entry.matchingType === winnerType;

                                                          return (
                                                              <MenuItem
                                                                  key={entry.matchingType}
                                                                  sx={{ p: 0 }}
                                                                  value={entry.matchingType}
                                                              >
                                                                  <Tooltip
                                                                      enterDelay={300}
                                                                      placement="right"
                                                                      PopperProps={{ style: { zIndex: 1500 } }}
                                                                      title={entry.details}
                                                                  >
                                                                      <Box sx={{
                                                                          display: 'flex',
                                                                          alignItems: 'center',
                                                                          justifyContent: 'space-between',
                                                                          gap: 2,
                                                                          width: '100%',
                                                                          p: 2,
                                                                      }}
                                                                      >
                                                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                              <Typography color="text.secondary" component="span" sx={{ minWidth: 20 }} variant="caption">
                                                                                  {`P${index + 1}`}
                                                                              </Typography>
                                                                              <span>{getMatcherLabel(entry.matchingType)}</span>
                                                                          </Box>
                                                                          {isWinner && (
                                                                          <Chip
                                                                              label="Selected"
                                                                              size="small"
                                                                              sx={{
                                                                                  height: 20,
                                                                                  fontSize: '0.7rem',
                                                                                  backgroundColor: 'primary.main',
                                                                                  color: 'white',
                                                                              }}
                                                                          />
                                                                          )}
                                                                          {available && !isWinner && (
                                                                          <Chip
                                                                              label="Available"
                                                                              size="small"
                                                                              sx={{
                                                                                  height: 20,
                                                                                  fontSize: '0.7rem',
                                                                                  borderColor: 'primary.main',
                                                                                  color: 'primary.main',
                                                                              }}
                                                                              variant="outlined"
                                                                          />
                                                                          )}
                                                                          {!available && (
                                                                          <Chip
                                                                              label="Unavailable"
                                                                              size="small"
                                                                              sx={{
                                                                                  height: 20,
                                                                                  fontSize: '0.7rem',
                                                                                  borderColor: 'error.main',
                                                                                  color: 'error.main',
                                                                              }}
                                                                              variant="outlined"
                                                                          />
                                                                          )}
                                                                      </Box>
                                                                  </Tooltip>
                                                              </MenuItem>
                                                          );
                                                      })}
                                                      {hasSiteResultPreview && [
                                                          <Divider key="site-result-divider" />,
                                                          <ListSubheader key="site-result-header" sx={{ lineHeight: '32px', fontSize: '0.75rem' }}>
                                                              Reference
                                                          </ListSubheader>,
                                                          <MenuItem key="siteResult" value="siteResult">
                                                              Site Result
                                                          </MenuItem>,
                                                      ]}
                                                  </Select>
                                              )}
                                          </Box>
                                          )}
                                          {!showSiteResult && selectedMatch && (
                                              <Alert
                                                  icon={false}
                                                  severity={selectedMatch.match ? 'info' : 'error'}
                                              >
                                                  <strong>{selectedMatch.match ? 'Match reason: ' : 'Match failure reason: '}</strong>
                                                  {selectedMatch.details}
                                              </Alert>
                                          )}
                                          {showSiteResult && matchingResults.pdsResultPreview.previewUrl && (
                                              <Box>
                                                  <Typography gutterBottom variant="h6">
                                                      Site Result Preview
                                                  </Typography>
                                                  <LoadingStateImage
                                                      imageSx={{
                                                          maxHeight: '400px',
                                                          maxWidth: '100%',
                                                          width: 'auto',
                                                          objectFit: 'contain',
                                                      }}
                                                      src={matchingResults.pdsResultPreview.previewUrl}
                                                  />
                                              </Box>
                                          )}

                                          {!showSiteResult && (
                                          <Box>
                                              {selectedMatch?.match && (
                                                  <>
                                                      <Box>
                                                          <Typography gutterBottom variant="h6">
                                                              Destination Preview
                                                          </Typography>
                                                          <LoadingStateImage
                                                              imageSx={{
                                                                  maxHeight: '400px',
                                                                  maxWidth: '100%',
                                                                  width: 'auto',
                                                                  objectFit: 'contain',
                                                              }}
                                                              src={selectedMatch.match.colorVariants[0]?.previewUrl
                                                                  ? `https:${selectedMatch.match.colorVariants[0].previewUrl}`
                                                                  : undefined}
                                                          />
                                                      </Box>
                                                      <Box sx={{ mt: 2 }}>
                                                          <DestinationProductOptions
                                                              destinationOptions={selectedMatch.match.completeProductOptions}
                                                              inputOptions={selectedOptions}
                                                          />
                                                      </Box>
                                                  </>
                                              )}
                                          </Box>
                                          )}
                                      </Box>

                                      {selectedMatch?.match && (
                                          <Divider sx={{ my: 2 }} />
                                      )}

                                      {matchingResults.documentSourcePreviewUrl && (
                                      <Box>
                                          <Typography gutterBottom variant="h6">
                                              Document Source Preview
                                          </Typography>
                                          <LoadingStateImage
                                              imageSx={{
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                  borderRadius: 1,
                                                  maxHeight: '400px',
                                                  maxWidth: '100%',
                                                  width: 'auto',
                                                  objectFit: 'contain',
                                              }}
                                              src={matchingResults.documentSourcePreviewUrl}
                                          />
                                      </Box>
                                      )}
                                      {isStaticDesign && originalSourceTemplateUrl && (
                                      <Box>
                                          <Typography gutterBottom variant="h6">
                                              Original Source Template Preview
                                          </Typography>
                                          <LoadingStateImage
                                              imageSx={{
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                  borderRadius: 1,
                                                  maxHeight: '400px',
                                                  maxWidth: '100%',
                                                  width: 'auto',
                                                  objectFit: 'contain',
                                              }}
                                              src={originalSourceTemplateUrl}
                                          />
                                      </Box>
                                      )}
                                      {matchingResults.dpcmResult.data && (
                                      <JsonViewer
                                          data={matchingResults.dpcmResult.data}
                                          title="Design Personalization Context"
                                      />
                                      )}
                                      {matchingResults.dpcmResult.data && (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                          <Typography gutterBottom variant="h6">
                                              Extracted Images / Logos
                                          </Typography>
                                          <AssetsImages
                                              assetsOriginalUrl={assetsOriginalUrl}
                                              assetsPreviewUrl={assetsPreviewUrl}
                                              assetsPrintUrl={assetsPrintUrl}
                                          />
                                      </Box>
                                      )}
                                  </Box>
                                  )}
                            </PlaceholderCard>
                        </RightColumn>
                    </TwoColumnLayout>
                </ContentWrapper>
            </StyledContainer>
        </>
    );
};
